import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { XtellarMark } from '@/src/components/XtellarMark'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { apiClient } from '@/lib/api/client'
import type { HelmInvitationPreview } from '@/lib/api/types'
import { createClient } from '@/lib/auth/client'
import {
  getHelmLoginUrl,
  getHelmSessionUser,
  requestHelmPasswordSession,
  type HelmSessionUser,
} from '@/src/lib/auth'

const ROLE_LABELS: Record<string, string> = {
  admin: 'administrator',
  accounts: 'accounts',
  viewer: 'viewer',
  forwarder: 'forwarding partner',
  customer: 'customer',
  warehouse: 'warehouse team',
}

type Loaded = { preview: HelmInvitationPreview; user: HelmSessionUser | null }

function Shell({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <Card className="w-full max-w-md rounded-lg">
        <CardHeader className="space-y-3">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <XtellarMark />
            <span className="text-sm font-semibold uppercase tracking-[0.14em]">Helm</span>
          </div>
          <CardTitle className="text-center">
            <h1 className="text-2xl font-semibold">{title}</h1>
          </CardTitle>
          {description && <CardDescription className="text-center">{description}</CardDescription>}
        </CardHeader>
        {children}
      </Card>
    </main>
  )
}

/**
 * Where an invitation link lands. It works before the person has an account:
 * a new person sets their name and password here; someone with an account
 * signs in and accepts; the signed-in invitee just accepts.
 */
export function InvitePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')?.trim() ?? ''
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!token) {
      setLoadError('This invitation link is incomplete. Open it again from the email.')
      return
    }
    let cancelled = false
    Promise.all([apiClient.previewHelmInvitation(token), getHelmSessionUser().catch(() => null)])
      .then(([preview, user]) => !cancelled && setLoaded({ preview, user }))
      .catch(
        (cause) =>
          !cancelled &&
          setLoadError(
            cause instanceof Error ? cause.message : 'This invitation link is not valid',
          ),
      )
    return () => {
      cancelled = true
    }
  }, [token])

  const finish = () => {
    setDone(true)
    window.setTimeout(() => navigate('/', { replace: true }), 1200)
  }

  async function register(event: FormEvent) {
    event.preventDefault()
    if (!loaded) return
    const errors: Record<string, string> = {}
    if (!name.trim()) errors.name = 'Enter your name.'
    if (password.length < 8) errors.password = 'Use at least 8 characters.'
    else if (password !== confirm) errors.confirm = 'The passwords do not match.'
    setFieldErrors(errors)
    if (Object.keys(errors).length) {
      document.getElementById(Object.keys(errors)[0]!)?.focus()
      return
    }
    setBusy(true)
    setError(null)
    try {
      await apiClient.registerFromHelmInvitation({ token, name: name.trim(), password })
      await requestHelmPasswordSession(loaded.preview.invited_email, password)
      finish()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The account could not be created')
    } finally {
      setBusy(false)
    }
  }

  async function accept() {
    setBusy(true)
    setError(null)
    try {
      await apiClient.acceptHelmInvitation(token)
      finish()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The invitation could not be accepted')
    } finally {
      setBusy(false)
    }
  }

  async function signOutAndRetry() {
    await createClient().auth.signOut()
    window.location.href = getHelmLoginUrl(`/invite?token=${encodeURIComponent(token)}`)
  }

  if (loadError) {
    return (
      <Shell title="Invitation not found">
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
        </CardContent>
      </Shell>
    )
  }
  if (!loaded) {
    return (
      <Shell title="Opening your invitation">
        <CardContent className="flex justify-center py-6">
          <Loader2 className="size-5 animate-spin text-muted-foreground" aria-label="Loading" />
        </CardContent>
      </Shell>
    )
  }

  const { preview, user } = loaded
  const role = ROLE_LABELS[preview.role] ?? preview.role
  const from = preview.invited_by_name
    ? `${preview.invited_by_name} invited you`
    : 'You were invited'
  const description = `${from} to join Helm as ${role}.`

  if (done) {
    return (
      <Shell title="Welcome to Helm" description="Taking you to the dashboard…">
        <CardContent>
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>You're in as {preview.invited_email}.</AlertDescription>
          </Alert>
        </CardContent>
      </Shell>
    )
  }

  if (preview.status !== 'PENDING' || preview.expired) {
    const message =
      preview.status === 'ACCEPTED'
        ? 'This invitation has already been used. Sign in to open Helm.'
        : preview.status === 'REJECTED'
          ? 'This invitation was withdrawn. Ask a Helm administrator for a new one.'
          : 'This invitation has expired. Ask a Helm administrator to send a new one.'
    return (
      <Shell title="This invitation can't be used" description={preview.invited_email}>
        <CardContent>
          <Alert variant={preview.status === 'ACCEPTED' ? 'default' : 'destructive'}>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        </CardContent>
        {preview.status === 'ACCEPTED' && (
          <CardFooter>
            <Button asChild className="w-full">
              <a href="/login">Sign in</a>
            </Button>
          </CardFooter>
        )}
      </Shell>
    )
  }

  const errorAlert = error && (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  )

  if (user) {
    const matches = (user.email ?? '').toLowerCase() === preview.invited_email.toLowerCase()
    return (
      <Shell title="Join Helm" description={description}>
        <CardContent className="space-y-4">
          {errorAlert}
          {matches ? (
            <p className="text-sm text-muted-foreground">
              You're signed in as {user.email}. Accept to get {role} access.
            </p>
          ) : (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This invitation is for {preview.invited_email}, but you're signed in as {user.email}
                . Sign out, then open the link again.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
        <CardFooter>
          {matches ? (
            <Button
              variant="brand"
              className="w-full"
              onClick={() => void accept()}
              disabled={busy}
            >
              {busy && <Loader2 className="animate-spin" />}
              Accept invitation
            </Button>
          ) : (
            <Button variant="outline" className="w-full" onClick={() => void signOutAndRetry()}>
              Sign out
            </Button>
          )}
        </CardFooter>
      </Shell>
    )
  }

  if (preview.account_exists) {
    return (
      <Shell title="Join Helm" description={description}>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {preview.invited_email} already has a Helm account. Sign in, and you'll come back here
            to accept.
          </p>
        </CardContent>
        <CardFooter>
          <Button asChild variant="brand" className="w-full">
            <a href={getHelmLoginUrl(`/invite?token=${encodeURIComponent(token)}`)}>
              Sign in to accept
            </a>
          </Button>
        </CardFooter>
      </Shell>
    )
  }

  return (
    <Shell title="Set up your Helm account" description={description}>
      <form onSubmit={register} noValidate>
        <CardContent className="space-y-5">
          {errorAlert}
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="invite-email">Email</FieldLabel>
              <Input id="invite-email" value={preview.invited_email} readOnly disabled />
              <FieldDescription>You'll sign in with this address.</FieldDescription>
            </Field>
            <Field data-invalid={Boolean(fieldErrors.name)}>
              <FieldLabel htmlFor="name" required>
                Your name
              </FieldLabel>
              <Input
                id="name"
                autoComplete="name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                aria-invalid={Boolean(fieldErrors.name)}
              />
              <FieldError>{fieldErrors.name}</FieldError>
            </Field>
            <Field data-invalid={Boolean(fieldErrors.password)}>
              <FieldLabel htmlFor="password" required>
                Password
              </FieldLabel>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-invalid={Boolean(fieldErrors.password)}
                aria-describedby="password-help"
              />
              <FieldDescription id="password-help">At least 8 characters.</FieldDescription>
              <FieldError>{fieldErrors.password}</FieldError>
            </Field>
            <Field data-invalid={Boolean(fieldErrors.confirm)}>
              <FieldLabel htmlFor="confirm" required>
                Confirm password
              </FieldLabel>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                aria-invalid={Boolean(fieldErrors.confirm)}
              />
              <FieldError>{fieldErrors.confirm}</FieldError>
            </Field>
          </FieldGroup>
        </CardContent>
        <CardFooter className="pt-6">
          <Button type="submit" variant="brand" className="w-full" disabled={busy}>
            {busy && <Loader2 className="animate-spin" />}
            Create account and join
          </Button>
        </CardFooter>
      </form>
    </Shell>
  )
}
